import { useState, useEffect } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { Search, SlidersHorizontal, SearchX, BellPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg, useFetch } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { btn, FilterChips, Loading, ErrorBox, EmptyState, Pagination, SelectField, TextField } from "./kit";

const KIND_LABEL = { job: "Jobs", project: "Projects", gov_job: "Government jobs", gov_scheme: "Schemes", gov_guide: "Guides" };

export function SaveSearchButton({ kind, params, chips }) {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const start = () => {
    setName(`${KIND_LABEL[kind]}${chips.length ? `: ${chips.map((c) => c.label.replace(/^[^:]+: /, "")).join(", ")}` : " (all)"}`.slice(0, 80));
    setErr("");
    setOpen(true);
  };
  const save = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { setErr("Give this search a name of at least 2 characters."); return; }
    const { page, sort, kind: _k, ...rest } = params;
    try { await api.post("/me/saved-searches", { kind, name: name.trim(), params: rest }); toast.success("Search saved. We'll notify you in-app when new matching listings are approved."); setOpen(false); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  if (!user) return <Link to={`/signin?next=${encodeURIComponent(loc.pathname + loc.search)}`} className={btn("secondary", "sm")} data-testid="save-search-signin"><BellPlus className="h-4 w-4" aria-hidden="true" />Sign in to save this search</Link>;
  return (
    <>
      <button className={btn("secondary", "sm")} onClick={start} data-testid="save-search-btn"><BellPlus className="h-4 w-4" aria-hidden="true" />Save search & get alerts</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="save-search-dialog">
          <DialogHeader><DialogTitle className="text-navy">Save this search</DialogTitle><DialogDescription>You'll get an in-app notification when a new listing matching these filters is approved. Manage alerts under Saved items.</DialogDescription></DialogHeader>
          <form onSubmit={save} noValidate className="space-y-4">
            <TextField id="saved-search-name" label="Name" required value={name} onChange={(e) => setName(e.target.value)} error={err} />
            {chips.length ? <p className="text-sm text-sub">Filters: {chips.map((c) => c.label).join(" · ")}</p> : <p className="text-sm text-sub">No filters — you'll be alerted about every new listing in this section.</p>}
            <div className="flex justify-end gap-3"><button type="button" className={btn("secondary")} onClick={() => setOpen(false)}>Cancel</button><button className={btn()} data-testid="save-search-submit">Save search</button></div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function useListing(endpoint, defaults = {}) {
  const [sp, setSp] = useSearchParams();
  const params = Object.fromEntries(sp.entries());
  const qs = new URLSearchParams({ ...defaults, ...params }).toString();
  const res = useFetch(`${endpoint}?${qs}`);
  const set = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v === "" || v == null || v === false) n.delete(k); else n.set(k, v);
    if (k !== "page") n.delete("page");
    setSp(n);
  };
  const clear = (keep = []) => {
    const n = new URLSearchParams();
    keep.forEach((k) => sp.get(k) && n.set(k, sp.get(k)));
    setSp(n);
  };
  const reset = (obj) => setSp(new URLSearchParams(obj));
  return { params, res, set, clear, reset };
}

export function SearchBar({ value, onSearch, placeholder, labelText = "Search" }) {
  const [q, setQ] = useState(value || "");
  useEffect(() => setQ(value || ""), [value]);
  return (
    <form role="search" onSubmit={(e) => { e.preventDefault(); onSearch(q.trim()); }} className="flex flex-col gap-2 sm:flex-row" data-testid="listing-search-form">
      <label htmlFor="page-search" className="sr-only">{labelText}</label>
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-sub" aria-hidden="true" />
        <input id="page-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="field !min-h-[52px] pl-10" data-testid="listing-search-input" />
      </div>
      <button className={btn("primary")} data-testid="listing-search-submit">Search</button>
    </form>
  );
}

export function FilterSelect({ k, params, set, ...rest }) {
  return <SelectField id={`filter-${k}`} value={params[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder="Any" {...rest} />;
}

export function ListingShell({ L, filters, chips, searchPlaceholder, sortOptions, renderItem, emptyTitle, emptyBody, emptyAction, keep = [], noun = "results", saveKind }) {
  const [showFilters, setShowFilters] = useState(false);
  const { params, res, set, clear } = L;
  const data = res.data;
  return (
    <div className="container-x py-8">
      <SearchBar value={params.q} onSearch={(v) => set("q", v)} placeholder={searchPlaceholder} />
      <div className="mt-6 grid gap-8 lg:grid-cols-[280px_1fr]">
        <div>
          <button className={`${btn("secondary")} w-full lg:hidden`} aria-expanded={showFilters} aria-controls="filters-panel" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn">
            <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />{showFilters ? "Hide filters" : "Show filters"}
          </button>
          <section id="filters-panel" aria-labelledby="filters-h" className={`${showFilters ? "block" : "hidden"} lg:block mt-4 lg:mt-0 card p-5 space-y-5`} data-testid="filters-panel">
            <h2 id="filters-h" className="text-lg font-bold">Filters</h2>
            {filters}
          </section>
        </div>
        <section aria-labelledby="results-h">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <h2 id="results-h" className="text-xl font-bold" aria-live="polite" data-testid="results-count">
              {res.loading ? "Searching…" : data ? `${data.total} ${noun}${params.q ? ` for “${params.q}”` : ""}` : ""}
            </h2>
            {sortOptions && <div className="sm:w-56"><SelectField id="sort" label="Sort by" required value={params.sort || "relevance"} onChange={(e) => set("sort", e.target.value)} options={sortOptions} /></div>}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3"><FilterChips chips={chips} onRemove={(k) => set(k, "")} onClear={() => clear(keep)} />{saveKind && <SaveSearchButton kind={saveKind} params={params} chips={chips} />}</div>
          <div className="mt-5">
            {res.loading && <Loading text="Loading results…" />}
            {res.error && <ErrorBox message={res.error} onRetry={res.reload} />}
            {data && !res.loading && (data.items.length ? (
              <ul className="grid gap-4 md:grid-cols-2" data-testid="results-list">{data.items.map((it) => <li key={it.id} className="flex [&>*]:w-full">{renderItem(it)}</li>)}</ul>
            ) : <EmptyState icon={SearchX} title={emptyTitle} body={emptyBody} action={emptyAction} testid="results-empty" />)}
            {data && <Pagination page={data.page} pages={data.pages} onChange={(p) => set("page", p)} />}
          </div>
        </section>
      </div>
    </div>
  );
}
